import { useTheme } from '@shared/providers/theme/ThemeContext';
import React, { useCallback, useRef, useState } from 'react';

interface CollapsibleCardProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  extraHeaderContent?: React.ReactNode;
  headerBackgroundColor?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

export const CollapsibleCardContext = React.createContext(false);

const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
  title,
  children,
  className,
  extraHeaderContent,
  headerBackgroundColor,
  collapsible = false,
  defaultExpanded = true,
}) => {
  const { colors } = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = useCallback(() => {
    if (collapsible) setExpanded(prev => !prev);
  }, [collapsible]);

  const hasHeader = title !== undefined && title !== null && title !== '';

  return (
    <CollapsibleCardContext.Provider value>
      <div className={`mb-3 shadow-md flex min-w-0 flex-col ${className || ''}`}>
        {hasHeader && (
          <div
            className={`flex items-center justify-between px-4 py-2 bg-white shadow-sm${expanded ? ' rounded-t' : ' rounded'}${collapsible ? ' cursor-pointer select-none' : ''}`}
            /*
             * A caller that passes `headerBackgroundColor` is saying something
             * with the colour — the patient sections paint the header black
             * while a history record is being created, and grey while one is
             * only being viewed. The attribute lets a tenant skin repaint the
             * ordinary header without repainting those: an inline style is
             * only reachable from CSS through `!important`, which cannot tell
             * the two cases apart on its own.
             */
            data-header-color={headerBackgroundColor ? 'custom' : undefined}
            style={{
              backgroundColor: headerBackgroundColor ?? colors.primary,
              color: colors.sectionHeaderText,
            }}
            onClick={toggleExpanded}
          >
            <span className="flex-1 text-base">{title}</span>
            {/*
             * Controls in the header (a 履歴 menu, an action button) must not
             * also fold the card when a collapsible header is clicked.
             */}
            <div className="flex items-center gap-2" onClick={event => event.stopPropagation()}>
              {extraHeaderContent}
              {collapsible && (
                /*
                 * Not a `<button>`: a read-only form wraps its sections in
                 * `<fieldset disabled>`, which disables every button inside it —
                 * and folding a section is reading, not editing, so it must keep
                 * working there. Keyboard behaviour is a button's.
                 */
                <span
                  role="button"
                  tabIndex={0}
                  // Folding a section is not a field: Enter moving through a form skips it (Tab still reaches it).
                  data-skip-enter-nav
                  className="collapsible-card-toggle flex cursor-pointer items-center gap-1 rounded border border-current bg-transparent px-2 py-0.5 text-sm"
                  aria-expanded={expanded}
                  onClick={toggleExpanded}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleExpanded();
                    }
                  }}
                >
                  {expanded ? '∧ 閉じる' : '∨ 開く'}
                </span>
              )}
            </div>
          </div>
        )}
        {collapsible ? (
          <div
            ref={contentRef}
            style={{
              // Fully open once expanded: a measured height would clip anything
              // the section grows by later (validation messages, toggled rows).
              maxHeight: expanded ? 'none' : 0,
              overflow: 'hidden',
              transition: 'max-height 0.3s ease',
            }}
          >
            <div
              className={`bg-white border-t border-gray-100${hasHeader ? ' rounded-b' : ' rounded'} shadow-sm`}
            >
              <div className="p-4">{children}</div>
            </div>
          </div>
        ) : (
          <div
            className={`bg-white border-t border-gray-100${hasHeader ? ' rounded-b' : ' rounded'} shadow-sm flex-1 flex min-w-0 flex-col`}
          >
            <div className="min-w-0 flex-1 p-4">{children}</div>
          </div>
        )}
      </div>
    </CollapsibleCardContext.Provider>
  );
};

export const useIsInsideCollapsibleCard = () => React.useContext(CollapsibleCardContext);

export default CollapsibleCard;
